import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import Login from "./pages/Login";
import AccessDenied from "./pages/AccessDenied";
import Layout from "./components/Layout";
import { usePlatformAdmin } from "./lib/usePlatformAdmin";

// Routable pages are lazy so the initial admin chunk only ships the
// landing page (Overview). Tremor's heavier AreaChart/BarList variants
// pull in only when the user navigates to a page that uses them.
const Overview = lazy(() => import("./pages/Overview"));
const Tokens = lazy(() => import("./pages/Tokens"));
const Companies = lazy(() => import("./pages/Companies"));
const CompanyDetail = lazy(() => import("./pages/CompanyDetail"));
const Users = lazy(() => import("./pages/Users"));
const UserDetail = lazy(() => import("./pages/UserDetail"));
const Accountants = lazy(() => import("./pages/Accountants"));
const Subscriptions = lazy(() => import("./pages/Subscriptions"));
const Server = lazy(() => import("./pages/Server"));
const Tax = lazy(() => import("./pages/Tax"));
const TaxJurisdiction = lazy(() => import("./pages/TaxJurisdiction"));
const Deploys = lazy(() => import("./pages/Deploys"));
const Audit = lazy(() => import("./pages/Audit"));

function PageFallback() {
  return (
    <div className="grid min-h-[60vh] place-items-center text-sm text-muted">
      Loading…
    </div>
  );
}

export default function App() {
  const { state } = usePlatformAdmin();

  if (state === "loading") {
    return (
      <div className="grid min-h-full place-items-center text-muted">
        Loading…
      </div>
    );
  }

  if (state === "unauthenticated") {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (state === "denied") {
    return (
      <Routes>
        <Route path="/access-denied" element={<AccessDenied />} />
        <Route path="*" element={<Navigate to="/access-denied" replace />} />
      </Routes>
    );
  }

  // state === "admin"
  return (
    <Layout>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/tokens" element={<Tokens />} />
          <Route path="/companies" element={<Companies />} />
          <Route path="/companies/:id" element={<CompanyDetail />} />
          <Route path="/users" element={<Users />} />
          <Route path="/users/:id" element={<UserDetail />} />
          <Route path="/accountants" element={<Accountants />} />
          <Route path="/subscriptions" element={<Subscriptions />} />
          <Route path="/server" element={<Server />} />
          <Route path="/tax" element={<Tax />} />
          <Route
            path="/tax/jurisdictions/:country/:code"
            element={<TaxJurisdiction />}
          />
          <Route path="/deploys" element={<Deploys />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/run" element={<RunStub />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

function RunStub() {
  return (
    <div className="space-y-3">
      <h1 className="text-xl font-semibold">Run</h1>
      <p className="text-sm text-muted">
        Phase 4. The whitelisted command runner is intentionally gated behind the
        read-only console being stable in production. See{" "}
        <code className="font-mono text-xs">docs/reference/OPS_PORTAL_PLAN.md</code>{" "}
        §4.
      </p>
    </div>
  );
}
